import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { MediaItem, MediaFit, SourceConfig } from "@gooner/shared";
import { api } from "../../lib/api";
import { Button } from "../ui/primitives";
import { Spinner } from "../ui/Spinner";
import { MediaSurface } from "../ui/MediaSurface";
import { IconReload, IconWarning } from "../ui/icons";

type State =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "empty" }
  | { phase: "error"; message: string }
  | { phase: "ready"; item: MediaItem };

/**
 * Live preview of a region's source: fetches the first item and renders it
 * (video or image) through the media proxy. Debounced + cancellable so rapid
 * source edits don't stack requests. Handles loading / empty / error states.
 */
export function SourcePreview({
  source,
  fit,
  muted,
  compact = false,
}: {
  source: SourceConfig;
  fit: MediaFit;
  muted: boolean;
  /** Fill the parent with no refresh chrome — for small thumbnails. */
  compact?: boolean;
}) {
  const [state, setState] = useState<State>({ phase: "idle" });
  const [nonce, setNonce] = useState(0);
  const reqId = useRef(0);

  // Serialize the source so the effect only refires on meaningful changes.
  const key = JSON.stringify(source);

  useEffect(() => {
    const id = ++reqId.current;
    setState({ phase: "loading" });
    const handle = setTimeout(() => {
      api
        .fetchSource(source)
        .then((res) => {
          if (reqId.current !== id) return;
          const item = res.items[0];
          setState(item ? { phase: "ready", item } : { phase: "empty" });
        })
        .catch((e: unknown) => {
          if (reqId.current !== id) return;
          setState({
            phase: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        });
    }, 350);
    return () => clearTimeout(handle);
    // `key` captures the source contents; `nonce` forces manual refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  if (compact) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-black">
        <PreviewBody state={state} fit={fit} muted={muted} />
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-[var(--color-edge)] bg-black">
      <PreviewBody state={state} fit={fit} muted={muted} />
      <div className="absolute right-2 top-2">
        <Button
          size="sm"
          variant="subtle"
          onClick={() => setNonce((n) => n + 1)}
          title="Refresh preview"
          className="bg-black/50 backdrop-blur"
        >
          <IconReload />
          Refresh
        </Button>
      </div>
    </div>
  );
}

function PreviewBody({
  state,
  fit,
  muted,
}: {
  state: State;
  fit: MediaFit;
  muted: boolean;
}) {
  if (state.phase === "loading" || state.phase === "idle") {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (state.phase === "empty") {
    return (
      <Centered>
        <p className="text-sm text-zinc-400">No media found for these tags.</p>
      </Centered>
    );
  }

  if (state.phase === "error") {
    return (
      <Centered>
        <IconWarning className="mb-1 text-amber-400" width={20} height={20} />
        <p className="text-xs text-zinc-400">Preview failed</p>
        <p className="mt-0.5 max-w-[90%] truncate text-[10px] text-zinc-600">
          {state.message}
        </p>
      </Centered>
    );
  }

  const { item } = state;
  // item.url / item.posterUrl already point wherever the server wants them to —
  // /api/media for catalog clips, or the admin-only RedGifs proxy for an import
  // preview. Play them as given; wrapping them again would double-proxy.
  return (
    <MediaSurface
      key={item.url}
      kind={item.kind}
      src={item.url}
      poster={item.posterUrl}
      fit={fit}
      loop
      muted={muted}
    />
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center">
      {children}
    </div>
  );
}
